import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { POST_TYPES, postTypeForCategory } from '../lib/cms'
import { api, type AdminAuthor, type AdminPost, type AdminStats } from './api'
import CountUp from './CountUp'
import Pager from './Pager'
import { useIsAdmin } from './role'
import ViewsDayChart from './ViewsDayChart'

const PAGE_SIZE = 10

function Glance({ label, value, hint }: { label: string; value: number | null; hint?: string }) {
  return (
    <div className="border border-rule px-4 py-4">
      <p className="text-caps text-[10px] text-muted">{label}</p>
      <p className="mt-2 font-serif text-[32px] leading-none tabular-nums">
        {value == null ? '…' : <CountUp value={value} />}
      </p>
      {hint ? <p className="mt-2 text-[12px] text-muted">{hint}</p> : null}
    </div>
  )
}

export default function AdminDashboard() {
  const [posts, setPosts] = useState<AdminPost[] | null>(null)
  const [people, setPeople] = useState<AdminAuthor[] | null>(null)
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [page, setPage] = useState(1)
  const isAdmin = useIsAdmin()

  useEffect(() => {
    api<AdminPost[]>('/api/admin/posts').then(setPosts).catch(() => setPosts([]))
    api<AdminAuthor[]>('/api/admin/authors').then(setPeople).catch(() => setPeople([]))
    // Editors don't see site statistics.
    if (isAdmin) api<AdminStats>('/api/admin/stats').then(setStats).catch(() => setStats(null))
  }, [isAdmin])

  const published = posts?.filter((post) => post.status === 'published').length ?? 0
  const scheduled = posts?.filter((post) => post.status === 'scheduled').length ?? 0
  const drafts = posts?.filter((post) => post.status === 'draft').length ?? 0
  const ranked = [...(posts ?? [])].sort((a, b) => b.updatedAt - a.updatedAt)
  const pages = Math.max(1, Math.ceil(ranked.length / PAGE_SIZE))
  const current = Math.min(page, pages)
  const visible = ranked.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  return (
    <section>
      <p className="text-caps text-[10px] text-rose">Studio</p>
      <h2 className="mt-1 font-serif text-[32px] leading-none font-medium">Dashboard</h2>
      <p className="mt-3 max-w-[48ch] text-sm text-muted">
        {isAdmin ? 'Pageviews and publishing at a glance.' : 'Your posts at a glance.'}
      </p>

      {isAdmin && stats ? (
        <div className="mt-8 border border-rule px-4 py-4">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-caps text-[10px] text-muted">Views, last 30 days</h3>
            <Link to="/admin/stats" className="text-[11px] tracking-[0.16em] text-muted uppercase hover:text-rose">
              Full statistics
            </Link>
          </div>
          <p className="mt-3 font-serif text-[36px] leading-none tabular-nums">
            <CountUp value={stats.totals.month} />
          </p>
          <ViewsDayChart days={stats.days} />
        </div>
      ) : null}

      <div className={`mt-3 grid grid-cols-2 gap-3 ${isAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
        <Glance label="Published" value={posts ? published : null} />
        <Glance label="Scheduled" value={posts ? scheduled : null} />
        <Glance label="Drafts" value={posts ? drafts : null} />
        {isAdmin ? <Glance label="Contributors" value={people ? people.length : null} /> : null}
      </div>

      <h3 className="text-caps mt-10 text-[11px] text-muted">Post types</h3>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {POST_TYPES.map((type) => {
          const count = posts?.filter((post) => post.category === type.category).length
          return (
            <Link key={type.slug} to={`/admin/${type.slug}`} className="border border-rule px-5 py-5 hover:border-ink">
              <p className="font-serif text-[22px] leading-tight">{type.menu}</p>
              <p className="mt-2 text-[12px] tracking-[0.12em] text-muted uppercase">
                {posts ? `${count} ${count === 1 ? type.singular.toLowerCase() : type.menu.toLowerCase()}` : '…'}
              </p>
            </Link>
          )
        })}
      </div>

      <div className="mt-10">
        <h3 className="text-caps text-[11px] text-muted">Latest posts</h3>
        {!posts ? (
          <p className="mt-4 text-sm text-muted">Loading posts…</p>
        ) : ranked.length === 0 ? (
          <p className="mt-4 text-sm text-muted">No posts in Studio yet.</p>
        ) : (
          <>
            <ul className="mt-5 divide-y divide-rule border-y border-rule">
              {visible.map((post) => {
                const type = postTypeForCategory(post.category)
                return (
                  <li key={post.id}>
                    <Link
                      to={`/admin/${type.slug}/${post.id}`}
                      className="flex items-baseline justify-between gap-4 py-2 text-[15px] leading-snug hover:text-rose"
                    >
                      <span className="min-w-0 truncate">{post.title}</span>
                      <span
                        className={`shrink-0 text-[11px] tracking-[0.16em] uppercase ${
                          post.status === 'published' ? 'text-rose' : post.status === 'scheduled' ? 'text-ink' : 'text-stone'
                        }`}
                      >
                        {post.status}
                      </span>
                    </Link>
                  </li>
                )
              })}
            </ul>
            <Pager page={current} pages={pages} onPage={setPage} />
          </>
        )}
      </div>
    </section>
  )
}
